import React from "react";

/**
 * Lab-investigations icons, exported STRAIGHT off the Figma boards via the
 * local Dev Mode asset server (node id noted per icon; path data verbatim).
 * Only the hardcoded fills/strokes are swapped for `currentColor` so each
 * placement can tint via CSS.
 *
 * The three digitise-modal glyphs (close / check / auto_awesome) are NOT
 * vectors on the boards — Figma renders them with the Material Icons FONT
 * (`font-['Material_Icons:Regular']` in the node's own export). Those three
 * use the canonical Material Icons outlines, i.e. the exact shapes that font
 * draws — there is no Figma vector to fetch for them.
 */

/** 3041:11685 "DA / Button · toolbar · plus" — the header "+ Add investigation". */
export const ToolbarPlusIcon = () => (
  <svg viewBox="0 0 12.4 12.4" fill="none" aria-hidden="true">
    <path
      d="M6.2 0.7V11.7M0.7 6.2H11.7"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** 3041:11680 — the 13×13 magnifier inside the card-header search box. */
export const HeaderSearchIcon = () => (
  <svg viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <circle cx="4.5" cy="4.5" r="3.75" stroke="currentColor" strokeWidth="1.5" />
    <path d="M8.7 8.7L12.3 12.3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/** 3041:14333 "upload" — the Digitise report button (Material glyph vector). */
export const UploadIcon = () => (
  <svg viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M7.0625 11.25V3.60938L4.625 6.04688L3.3125 4.6875L8 0L12.6875 4.6875L11.375 6.04688L8.9375 3.60938V11.25H7.0625ZM2.375 15C1.85937 15 1.41797 14.8164 1.05078 14.4492C0.683594 14.082 0.5 13.6406 0.5 13.125V10.3125H2.375V13.125H13.625V10.3125H15.5V13.125C15.5 13.6406 15.3164 14.082 14.9492 14.4492C14.582 14.8164 14.1406 15 13.625 15H2.375Z"
      fill="currentColor"
    />
  </svg>
);

/** 3041:11917 "icon/plus" — the Add manually button. */
export const PlusIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3.33301 8H12.6663M8 3.33333V12.6667"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** 3041:12097 "icon/flask-conical" — the empty-state medallion. */
export const FlaskIcon = () => (
  <svg viewBox="0 0 26 26" fill="none" aria-hidden="true">
    <path
      d="M15.1667 2.16667V8.66667C15.1665 9.03011 15.2578 9.38775 15.4321 9.70667L21.4012 20.6267C21.5818 20.9567 21.6733 21.328 21.6668 21.7041C21.6604 22.0802 21.5561 22.4481 21.3642 22.7717C21.1724 23.0952 20.8996 23.3633 20.5727 23.5494C20.2459 23.7356 19.8762 23.8334 19.5 23.8333H6.5C6.12384 23.8334 5.75414 23.7356 5.42727 23.5494C5.1004 23.3633 4.82763 23.0952 4.63579 22.7717C4.44395 22.4481 4.33965 22.0802 4.33315 21.7041C4.32666 21.328 4.41819 20.9567 4.59875 20.6267L10.5679 9.70667C10.7422 9.38775 10.8335 9.03011 10.8333 8.66667V2.16667"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M6.99121 16.25H19.0097" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9.20801 2.16666H16.7913" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** 3041:11931 "icon/chev-up-16" — the 9.7×5.7 vector centred in its 16 box. */
export const ChevronUpIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M4 10L8 6L12 10"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** 3041:11933 "search" — the Investigation field's magnifier (Material glyph vector). */
export const FieldSearchIcon = () => (
  <svg viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M13.0667 14L8.86667 9.8C8.53333 10.0667 8.15 10.2778 7.71667 10.4333C7.28333 10.5889 6.82222 10.6667 6.33333 10.6667C5.12222 10.6667 4.09722 10.2472 3.25833 9.40833C2.41944 8.56944 2 7.54444 2 6.33333C2 5.12222 2.41944 4.09722 3.25833 3.25833C4.09722 2.41944 5.12222 2 6.33333 2C7.54444 2 8.56944 2.41944 9.40833 3.25833C10.2472 4.09722 10.6667 5.12222 10.6667 6.33333C10.6667 6.82222 10.5889 7.28333 10.4333 7.71667C10.2778 8.15 10.0667 8.53333 9.8 8.86667L14 13.0667L13.0667 14ZM6.33333 9.33333C7.16667 9.33333 7.875 9.04167 8.45833 8.45833C9.04167 7.875 9.33333 7.16667 9.33333 6.33333C9.33333 5.5 9.04167 4.79167 8.45833 4.20833C7.875 3.625 7.16667 3.33333 6.33333 3.33333C5.5 3.33333 4.79167 3.625 4.20833 4.20833C3.625 4.79167 3.33333 5.5 3.33333 6.33333C3.33333 7.16667 3.625 7.875 4.20833 8.45833C4.79167 9.04167 5.5 9.33333 6.33333 9.33333Z"
      fill="currentColor"
    />
  </svg>
);

/** 3041:11947 "calendar_today" — the Test date field (Material glyph vector). */
export const CalendarIcon = () => (
  <svg viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M13.333 2.00002H12.6663V0.666687H11.333V2.00002H4.66634V0.666687H3.33301V2.00002H2.66634C1.93301 2.00002 1.33301 2.60002 1.33301 3.33335V14C1.33301 14.7334 1.93301 15.3334 2.66634 15.3334H13.333C14.0663 15.3334 14.6663 14.7334 14.6663 14V3.33335C14.6663 2.60002 14.0663 2.00002 13.333 2.00002ZM13.333 14H2.66634V6.66669H13.333V14ZM13.333 5.33335H2.66634V3.33335H13.333V5.33335Z"
      fill="currentColor"
    />
  </svg>
);

/** 3112:2677 "icon/stethoscope" — the hospitalisation empty-state mark. */
export const StethoscopeIcon = () => (
  <svg viewBox="0 0 26 26" fill="none" aria-hidden="true">
    <path d="M11.9167 2.16667V4.33333M5.41667 2.16667V4.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path
      d="M5.41667 3.25H4.33333C3.7587 3.25 3.2076 3.47827 2.80127 3.8846C2.39494 4.29093 2.16667 4.84203 2.16667 5.41667V9.75C2.16667 11.4739 2.85149 13.1272 4.07047 14.3462C5.28946 15.5652 6.94276 16.25 8.66667 16.25C10.3906 16.25 12.0439 15.5652 13.2629 14.3462C14.4818 13.1272 15.1667 11.4739 15.1667 9.75V5.41667C15.1667 4.84203 14.9384 4.29093 14.5321 3.8846C14.1257 3.47827 13.5746 3.25 13 3.25H11.9167"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
    />
    <path
      d="M8.66667 16.25C8.66667 17.9739 9.35149 19.6272 10.5705 20.8462C11.7895 22.0652 13.4428 22.75 15.1667 22.75C16.8906 22.75 18.5439 22.0652 19.7629 20.8462C20.9818 19.6272 21.6667 17.9739 21.6667 16.25V13"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
    />
    <path
      d="M21.6667 13C22.8633 13 23.8333 12.0299 23.8333 10.8333C23.8333 9.63672 22.8633 8.66667 21.6667 8.66667C20.47 8.66667 19.5 9.63672 19.5 10.8333C19.5 12.0299 20.47 13 21.6667 13Z"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
    />
  </svg>
);

/** 3408:2761 "delete" — the allergies row trash (board fill #E44437). */
export const TrashIcon = () => (
  <svg viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M4.66663 14C4.29996 14 3.98607 13.8694 3.72496 13.6083C3.46385 13.3472 3.33329 13.0333 3.33329 12.6667V4H2.66663V2.66667H5.99996V2H9.99996V2.66667H13.3333V4H12.6666V12.6667C12.6666 13.0333 12.5361 13.3472 12.275 13.6083C12.0138 13.8694 11.7 14 11.3333 14H4.66663ZM11.3333 4H4.66663V12.6667H11.3333V4ZM5.99996 11.3333H7.33329V5.33333H5.99996V11.3333ZM8.66663 11.3333H9.99996V5.33333H8.66663V11.3333Z"
      fill="currentColor"
    />
  </svg>
);

/** 3071:3271 "icon/plus" — the 12×12 plus inside a SUGGESTED reaction chip. */
export const ChipPlusIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path
      d="M6 1.38462V10.6154M1.38462 6H10.6154"
      stroke="currentColor"
      strokeWidth="1.56923"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** 3976:15074 "icon/section/allergies" — the empty-state medallion mark
 *  (warning triangle + exclamation, board strokes #6B7280 → currentColor). */
export const TriangleAlertIcon = () => (
  <svg viewBox="0 0 26 26" fill="none" aria-hidden="true">
    <path
      d="M13 3.9L22.75 20.8H3.25L13 3.9Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M13 10.4V14.95" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="12.998" cy="17.81" r="1.105" fill="currentColor" />
  </svg>
);

/** 3296:2900 "icon/calendar" — the stroke-outline calendar on the allergy
 *  date fields (a different glyph from the Material `calendar_today` above). */
export const CalendarOutlineIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M2 3.2H14V14.2H2V3.2Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2 6.4H14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5 1.8V4.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M11 1.8V4.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** 3410:2789 "Close button" ✕ glyph — the delete-dialog close (the button's
 *  white fill / #e5e7eb border / r6 come from CSS; this is just the cross). */
export const DialogCloseIcon = () => (
  <svg viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <path d="M7 7L21 21M21 7L7 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/** 3976:15063 "icon/section/family" — the family-history empty-state
 *  medallion mark (three heads + shoulders; board strokes #6B7280 @1.5). */
export const FamilyMarkIcon = () => (
  <svg viewBox="0 0 26 26" fill="none" aria-hidden="true">
    <circle cx="13" cy="7.8" r="1.85" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="5.85051" cy="9.74999" r="1.33" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="20.1505" cy="9.74999" r="1.33" stroke="currentColor" strokeWidth="1.5" />
    <path d="M8.45 20.8C11.4833 17.7667 14.5167 17.7667 17.55 20.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2.6 18.85C3.9 16.6833 5.2 15.8167 6.5 16.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M23.4 18.85C22.1 16.6833 20.8 15.8167 19.5 16.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** 3071:2771 "icon/chevron-down" — the family-history relationship select. */
export const ChevronDownIcon = () => (
  <svg viewBox="0 0 9.4 5.4" fill="none" aria-hidden="true">
    <path
      d="M0.7 0.7L4.7 4.7L8.7 0.7"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** 3071:2797 "icon/sparkle" — the "Common conditions" mark (board stroke
 *  #5D1B12, kept as an explicit colour at the call site). */
export const SparkleMarkIcon = () => (
  <svg viewBox="0 0 8.6 8.6" fill="none" aria-hidden="true">
    <path
      d="M4.3 0.7L5.3 3.3L7.9 4.3L5.3 5.3L4.3 7.9L3.3 5.3L0.7 4.3L3.3 3.3L4.3 0.7Z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** 3071:2800 "icon/plus" — the 12px plus inside an unselected quick-pick
 *  chip (1.6 stroke, distinct from the 1.57 chip plus on the allergy board). */
export const ChipPlusBoldIcon = () => (
  <svg viewBox="0 0 10.6 10.6" fill="none" aria-hidden="true">
    <path
      d="M5.3 0.8V9.8M0.8 5.3H9.8"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** 4064:3097 renders the Material Icons font ligature "history" — the
 *  "Deleted entries" toolbar glyph. Canonical Material outline. */
export const HistoryGlyph = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M13 3a9 9 0 0 0-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42A8.954 8.954 0 0 0 13 21a9 9 0 0 0 0-18zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"
      fill="currentColor"
    />
  </svg>
);

/* ---- Material Icons FONT glyphs (see file header) ---- */

/** 3060:1770 renders the font glyph "close". */
export const CloseGlyph = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
      fill="currentColor"
    />
  </svg>
);

/** 3370:2904 renders the font glyph "check". */
export const CheckGlyph = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor" />
  </svg>
);

/** 3060:1785 / 3060:1793 render the font glyph "auto_awesome". */
export const SparkleGlyph = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="m19 9 1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z"
      fill="currentColor"
    />
  </svg>
);
